import {
  PhotoCard,
  PhotoCardProps,
} from "@/modes/walk-mode/photo-preview-list";
import { FaroText, NO_TRANSLATE_CLASS } from "@faro-lotv/flat-ui";
import { assert, convertToTimeString } from "@faro-lotv/foundation";
import { GUID, IElementUnassigned2dPhoto } from "@faro-lotv/ielement-types";
import { Stack } from "@mui/system";
import type { JSX } from "react";

type Unassigned2dPhotosGroupCardProps = {
  /** The name of the group */
  name: string;
  /** The unassigned 2D photo group to display */
  clusters: IElementUnassigned2dPhoto[][];
  /** The list of currently selected photo IDs */
  selectedPhotos: GUID[];
  /** Function to update the selection state of a photo */
  updateSelection: PhotoCardProps["onPhotoSelected"];
};

/**
 * Generates a time frame label for a cluster of unassigned 2D photos.
 *
 * @param cluster - An array of unassigned 2D photos, must contain at least one element
 * @returns A string representing the time frame. For a single photo, returns the creation time.
 *          For multiple photos, returns a range from the first to the last photo's creation time.
 */
function generateTimeFrameClusterLabel(
  cluster: IElementUnassigned2dPhoto[],
): string {
  assert(cluster.length > 0, "Cluster should contain at least one photo");
  if (cluster.length === 1) {
    return `${convertToTimeString(cluster[0].createdAt)}`;
  }
  return `${convertToTimeString(cluster[0].createdAt)} - ${convertToTimeString(cluster[cluster.length - 1].createdAt)}`;
}

/**
 * @returns card component that displays information about unassigned 2D photo groups.
 * @param props - The component props
 * @param props.name - The name of the unassigned photo group
 * @param props.clusters - An array of clusters belonging to this group
 */
export function Unassigned2dPhotosGroupCard({
  name,
  clusters,
  selectedPhotos,
  updateSelection,
}: Unassigned2dPhotosGroupCardProps): JSX.Element {
  return (
    <Stack direction="column">
      <FaroText className={NO_TRANSLATE_CLASS} variant="bodyM" sx={{ mb: 1 }}>
        {name}
      </FaroText>
      <Stack sx={{ pl: 2 }}>
        {clusters.map((cluster, index) => (
          <Stack key={index} direction="column" sx={{ gap: 1 }}>
            <FaroText className={NO_TRANSLATE_CLASS} variant="bodyM">
              {generateTimeFrameClusterLabel(cluster)}
            </FaroText>
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              {cluster.map((photo) => (
                <PhotoCard
                  key={photo.id}
                  imgElement={photo}
                  selected={selectedPhotos.includes(photo.id)}
                  imageHeight={58}
                  onPhotoSelected={updateSelection}
                />
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
